/**
 * Price Comparison Agent
 * Compares prices across farms, tracks seasonal price trends, provides cost insights
 */

interface PricePoint {
  farmId: string;
  farmName: string;
  productName: string;
  price: number;
  currency: string;
  unit: string;
  date: Date;
  season?: string;
}

interface PriceTrend {
  productName: string;
  averagePrice: number;
  minPrice: number;
  maxPrice: number;
  priceRange: number;
  lowestFarm: { id: string; name: string; price: number };
  highestFarm: { id: string; name: string; price: number };
  priceChangePercent?: number;
}

interface SeasonalPriceTrend {
  productName: string;
  season: string;
  averagePrice: number;
  expectedRange: { min: number; max: number };
  trend: 'rising' | 'falling' | 'stable';
}

interface PriceInsight {
  message: string;
  type: 'opportunity' | 'warning' | 'info';
  savings?: number;
  percentSavings?: number;
}

class PriceComparisonAgent {
  private priceHistory: PricePoint[] = [];

  /**
   * Add price data point
   */
  recordPrice(pricePoint: PricePoint): void {
    this.priceHistory.push(pricePoint);
  }

  /**
   * Record multiple prices at once
   */
  recordPrices(pricePoints: PricePoint[]): void {
    this.priceHistory.push(...pricePoints);
  }

  /**
   * Compare prices for a specific product across farms
   */
  comparePricesForProduct(productName: string): PriceTrend {
    const relevantPrices = this.priceHistory.filter(
      (p) =>
        p.productName.toLowerCase() === productName.toLowerCase() &&
        p.date >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // Last 30 days
    );

    if (relevantPrices.length === 0) {
      return {
        productName,
        averagePrice: 0,
        minPrice: 0,
        maxPrice: 0,
        priceRange: 0,
        lowestFarm: { id: '', name: '', price: 0 },
        highestFarm: { id: '', name: '', price: 0 },
      };
    }

    const prices = relevantPrices.map((p) => p.price);
    const averagePrice =
      prices.reduce((a, b) => a + b, 0) / prices.length;
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    const lowestFarmData = relevantPrices.find((p) => p.price === minPrice);
    const highestFarmData = relevantPrices.find((p) => p.price === maxPrice);

    return {
      productName,
      averagePrice,
      minPrice,
      maxPrice,
      priceRange: maxPrice - minPrice,
      lowestFarm: {
        id: lowestFarmData?.farmId || '',
        name: lowestFarmData?.farmName || '',
        price: minPrice,
      },
      highestFarm: {
        id: highestFarmData?.farmId || '',
        name: highestFarmData?.farmName || '',
        price: maxPrice,
      },
    };
  }

  /**
   * Get farm's pricing compared to market average
   */
  getFarmPriceComparison(
    farmId: string,
    farmName: string
  ): {
    farmId: string;
    farmName: string;
    products: {
      productName: string;
      farmPrice: number;
      averagePrice: number;
      pricePosition: 'below_average' | 'at_average' | 'above_average';
      savingsPercent: number;
    }[];
    competitiveness: number;
  } {
    // Get all products this farm sells
    const farmProducts = this.priceHistory
      .filter(
        (p) =>
          p.farmId === farmId &&
          p.date >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      )
      .map((p) => p.productName);

    const uniqueProducts = [...new Set(farmProducts)];

    const productComparisons = uniqueProducts.map((product) => {
      const farmPrice = this.priceHistory.find(
        (p) =>
          p.farmId === farmId &&
          p.productName === product &&
          p.date >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      )?.price;

      const trend = this.comparePricesForProduct(product);

      const savingsPercent =
        trend.averagePrice > 0
          ? ((trend.averagePrice - (farmPrice || trend.averagePrice)) /
              trend.averagePrice) *
            100
          : 0;

      let pricePosition: 'below_average' | 'at_average' | 'above_average' =
        'at_average';
      if (savingsPercent > 5) {
        pricePosition = 'below_average';
      } else if (savingsPercent < -5) {
        pricePosition = 'above_average';
      }

      return {
        productName: product,
        farmPrice: farmPrice || 0,
        averagePrice: trend.averagePrice,
        pricePosition,
        savingsPercent,
      };
    });

    // Calculate competitiveness score
    const competitiveness =
      productComparisons.length > 0
        ? (productComparisons.filter((p) => p.pricePosition !== 'above_average')
            .length /
            productComparisons.length) *
          100
        : 0;

    return {
      farmId,
      farmName,
      products: productComparisons,
      competitiveness,
    };
  }

  /**
   * Track seasonal price trends
   */
  analyzeSeasonalTrends(productName: string): SeasonalPriceTrend[] {
    const relevantPrices = this.priceHistory.filter(
      (p) => p.productName.toLowerCase() === productName.toLowerCase()
    );

    if (relevantPrices.length === 0) {
      return [];
    }

    // Group by season
    const seasons = {
      winter: [12, 1, 2],
      spring: [3, 4, 5],
      summer: [6, 7, 8],
      fall: [9, 10, 11],
    };

    const trends: SeasonalPriceTrend[] = [];

    for (const [seasonName, months] of Object.entries(seasons)) {
      const seasonalPrices = relevantPrices.filter((p) =>
        months.includes(p.date.getMonth() + 1)
      );

      if (seasonalPrices.length > 0) {
        const prices = seasonalPrices.map((p) => p.price);
        const averagePrice =
          prices.reduce((a, b) => a + b, 0) / prices.length;
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        // Determine trend (simplified - in real app would look at time series)
        let trend: 'rising' | 'falling' | 'stable' = 'stable';
        if (seasonalPrices.length > 1) {
          const firstHalf = seasonalPrices.slice(
            0,
            Math.floor(seasonalPrices.length / 2)
          );
          const secondHalf = seasonalPrices.slice(
            Math.floor(seasonalPrices.length / 2)
          );

          const firstAvg =
            firstHalf.reduce((a, b) => a + b.price, 0) / firstHalf.length;
          const secondAvg =
            secondHalf.reduce((a, b) => a + b.price, 0) / secondHalf.length;

          if (secondAvg > firstAvg * 1.05) {
            trend = 'rising';
          } else if (secondAvg < firstAvg * 0.95) {
            trend = 'falling';
          }
        }

        trends.push({
          productName,
          season: seasonName,
          averagePrice,
          expectedRange: { min: minPrice, max: maxPrice },
          trend,
        });
      }
    }

    return trends;
  }

  /**
   * Get price insights and recommendations
   */
  getPriceInsights(productName: string, budget?: number): PriceInsight[] {
    const insights: PriceInsight[] = [];
    const comparison = this.comparePricesForProduct(productName);

    if (comparison.averagePrice === 0) {
      insights.push({
        message: `No pricing data available for ${productName}`,
        type: 'info',
      });
      return insights;
    }

    // Price variation insight
    const priceVariation =
      (comparison.priceRange / comparison.averagePrice) * 100;
    if (priceVariation > 30) {
      insights.push({
        message: `High price variation (${priceVariation.toFixed(1)}%) for ${productName} - shop around`,
        type: 'opportunity',
        savings: comparison.priceRange,
        percentSavings: priceVariation,
      });
    }

    // Budget alert
    if (budget && comparison.minPrice > budget) {
      insights.push({
        message: `Lowest price for ${productName} (${comparison.minPrice}) exceeds your budget (${budget})`,
        type: 'warning',
      });
    } else if (budget && comparison.minPrice <= budget) {
      const savings = budget - comparison.minPrice;
      insights.push({
        message: `You can save $${savings.toFixed(2)} per unit at ${comparison.lowestFarm.name}`,
        type: 'opportunity',
        savings,
        percentSavings: (savings / budget) * 100,
      });
    }

    // Seasonal insight
    const seasonalTrends = this.analyzeSeasonalTrends(productName);
    const currentMonth = new Date().getMonth() + 1;
    const currentSeason = this.getSeasonForMonth(currentMonth);
    const currentSeasonalTrend = seasonalTrends.find(
      (t) => t.season === currentSeason
    );

    if (currentSeasonalTrend) {
      if (currentSeasonalTrend.trend === 'rising') {
        insights.push({
          message: `${productName} prices are rising this season - consider buying now`,
          type: 'opportunity',
        });
      } else if (currentSeasonalTrend.trend === 'falling') {
        insights.push({
          message: `${productName} prices are falling this season`,
          type: 'info',
        });
      }
    }

    return insights;
  }

  /**
   * Get season for a given month
   */
  private getSeasonForMonth(month: number): string {
    if ([12, 1, 2].includes(month)) return 'winter';
    if ([3, 4, 5].includes(month)) return 'spring';
    if ([6, 7, 8].includes(month)) return 'summer';
    return 'fall';
  }

  /**
   * Calculate bulk purchase discount recommendations
   */
  calculateBulkSavings(
    productName: string,
    quantity: number,
    bulkDiscountPercent: number = 10
  ): {
    productName: string;
    quantity: number;
    regularPrice: number;
    bulkPrice: number;
    totalSavings: number;
  } {
    const comparison = this.comparePricesForProduct(productName);

    return {
      productName,
      quantity,
      regularPrice: comparison.minPrice * quantity,
      bulkPrice: (comparison.minPrice * (1 - bulkDiscountPercent / 100)) * quantity,
      totalSavings: (comparison.minPrice * (bulkDiscountPercent / 100)) * quantity,
    };
  }

  /**
   * Find best deals currently available
   */
  getBestDeals(limit: number = 10): {
    productName: string;
    farm: { id: string; name: string };
    price: number;
    savingsPercent: number;
  }[] {
    const recentPrices = this.priceHistory.filter(
      (p) => p.date >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    );

    const deals = recentPrices
      .map((price) => {
        const comparison = this.comparePricesForProduct(price.productName);
        const savingsPercent =
          comparison.averagePrice > 0
            ? ((comparison.averagePrice - price.price) /
                comparison.averagePrice) *
              100
            : 0;

        return {
          productName: price.productName,
          farm: { id: price.farmId, name: price.farmName },
          price: price.price,
          savingsPercent,
        };
      })
      .sort((a, b) => b.savingsPercent - a.savingsPercent);

    return deals.slice(0, limit);
  }

  /**
   * Clear price history
   */
  clearPriceHistory(): void {
    this.priceHistory = [];
  }
}

export default new PriceComparisonAgent();
